import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { Add, Delete, Edit, Search, Settings } from "@mui/icons-material"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { FC, useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateConfigurationMutation,
  useDeleteConfigurationMutation,
  useGetConfigurationsQuery,
  useUpdateConfigurationMutation,
} from "../../apis/appApi"

type SettingFormData = {
  name: string
  value: string
  enabled: boolean
}

const emptyForm: SettingFormData = { name: "", value: "", enabled: true }

const getSettingGroup = (name: string): string => {
  const n = name.toLowerCase()

  if (
    n.includes("sms") ||
    n.includes("email") ||
    n.includes("notif") ||
    n.includes("alert")
  ) {
    return "Notifications"
  }

  if (
    n.includes("api") ||
    n.includes("token") ||
    n.includes("webhook") ||
    n.includes("integration")
  ) {
    return "Integrations"
  }

  if (
    n.includes("ui") ||
    n.includes("theme") ||
    n.includes("display") ||
    n.includes("color")
  ) {
    return "Display"
  }

  if (
    n.includes("auth") ||
    n.includes("password") ||
    n.includes("security") ||
    n.includes("session")
  ) {
    return "Security"
  }

  if (
    n.includes("order") ||
    n.includes("delivery") ||
    n.includes("pickup") ||
    n.includes("workflow")
  ) {
    return "Operations"
  }

  return "General"
}

export const SystemSettingsTab: FC = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { data: configurations, isLoading } = useGetConfigurationsQuery()
  const [createConfiguration] = useCreateConfigurationMutation()
  const [updateConfiguration] = useUpdateConfigurationMutation()
  const [deleteConfiguration] = useDeleteConfigurationMutation()

  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<SettingFormData>(emptyForm)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [searchText, setSearchText] = useState("")
  const [selectedSetting, setSelectedSetting] = useState<any | null>(null)

  const handleCreate = useCallback(() => {
    setEditingId(null)
    setForm(emptyForm)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((config: any) => {
    setEditingId(config.id)
    setForm({
      name: config.name ?? "",
      value: config.value ?? "",
      enabled: config.enabled ?? true,
    })
    setFormOpen(true)
  }, [])

  const handleFormClose = useCallback(() => {
    setFormOpen(false)
    setEditingId(null)
    setForm(emptyForm)
  }, [])

  const handleFormSubmit = async () => {
    if (editingId) {
      await updateConfiguration({ id: editingId, body: form })
    } else {
      await createConfiguration(form)
    }
    handleFormClose()
  }

  const handleToggleEnabled = async (config: any) => {
    await updateConfiguration({
      id: config.id,
      body: { enabled: !config.enabled },
    })
  }

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId) {
      await deleteConfiguration(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }

  const rows = configurations ?? []
  const filteredRows = rows.filter((config: any) =>
    (config.name ?? "").toLowerCase().includes(searchText.toLowerCase().trim()),
  )
  const groupedRows = filteredRows.reduce(
    (acc: Record<string, any[]>, config: any) => {
      const group = getSettingGroup(config.name ?? "")
      if (!acc[group]) acc[group] = []
      acc[group].push(config)
      return acc
    },
    {},
  )
  const orderedGroups = [
    "Operations",
    "Notifications",
    "Integrations",
    "Display",
    "Security",
    "General",
  ].filter((group) => groupedRows[group]?.length > 0)
  const hasNoData = rows.length === 0
  const hasNoSearchMatch = !hasNoData && filteredRows.length === 0

  return (
    <Box
      {...uiSurface("UI-DLV2-171")}
      data-testid="settings-tab"
      sx={{ p: { xs: 2, md: 3 }, overflow: "visible", minHeight: "fit-content" }}
    >
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {t("configuration.settings.title")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(
              "configuration.settings.subtitle",
              "Manage global system configurations",
            )}
          </Typography>
        </Box>
        <Button
          data-testid="settings-create-btn"
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
          sx={{ borderRadius: 2, textTransform: "none", px: 3 }}
        >
          {t("configuration.settings.create")}
        </Button>
      </Stack>

      <TextField
        data-testid="settings-search-input"
        placeholder={t(
          "configuration.settings.searchPlaceholder",
          "Search settings by name",
        )}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 3 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          },
        }}
      />

      {/* Content */}
      {isLoading ? (
        <Box
          data-testid="settings-loading"
          sx={{ display: "flex", justifyContent: "center", py: 8 }}
        >
          <CircularProgress />
        </Box>
      ) : hasNoData ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <Stack
            data-testid="settings-empty-state"
            alignItems="center"
            spacing={2}
            sx={{ maxWidth: 400, textAlign: "center" }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "primary.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1,
              }}
            >
              <Settings sx={{ fontSize: 40, color: "primary.main" }} />
            </Box>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              {t("configuration.settings.empty")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("configuration.settings.emptyDescription")}
            </Typography>
            <Button
              data-testid="settings-empty-create-btn"
              variant="outlined"
              startIcon={<Add />}
              onClick={handleCreate}
              sx={{ borderRadius: 2, textTransform: "none" }}
            >
              {t("configuration.settings.create")}
            </Button>
          </Stack>
        </Box>
      ) : hasNoSearchMatch ? (
        <Paper
          variant="outlined"
          sx={{ p: 4, borderRadius: 2, textAlign: "center" }}
        >
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {t("configuration.settings.noSearchResult", "No matching setting")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t(
              "configuration.settings.noSearchResultDescription",
              "Try another keyword to find the setting you need.",
            )}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2.5}>
          {orderedGroups.map((groupName) => {
            const groupItems = groupedRows[groupName] ?? []

            return (
              <Box key={groupName}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ mb: 1.5 }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    color="text.primary"
                  >
                    {groupName}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600 }}
                  >
                    {groupItems.length}
                  </Typography>
                </Stack>

                {isMobile ? (
                  /* Mobile: card list */
                  <Stack
                    data-testid={`settings-table-${groupName.toLowerCase()}`}
                    spacing={1}
                  >
                    {groupItems.map((config: any) => (
                      <Paper
                        key={config.id}
                        data-testid={`settings-row-${config.id}`}
                        variant="outlined"
                        onClick={() => setSelectedSetting(config)}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          cursor: "pointer",
                          "&:hover": { bgcolor: "action.hover" },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ flex: 1, mr: 1, wordBreak: "break-word" }}
                          >
                            {config.name}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <Switch
                              data-testid={`settings-toggle-${config.id}`}
                              size="small"
                              checked={config.enabled}
                              onChange={(event) => {
                                event.stopPropagation()
                                handleToggleEnabled(config)
                              }}
                              color="primary"
                            />
                            <IconButton
                              data-testid={`settings-edit-${config.id}-btn`}
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation()
                                handleEdit(config)
                              }}
                              color="primary"
                              sx={{
                                bgcolor: "primary.50",
                                "&:hover": { bgcolor: "primary.100" },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              data-testid={`settings-delete-${config.id}-btn`}
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation()
                                setDeleteConfirmId(config.id)
                              }}
                              color="error"
                              sx={{
                                bgcolor: "error.50",
                                "&:hover": { bgcolor: "error.100" },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mt: 0.5,
                            wordBreak: "break-all",
                          }}
                        >
                          {config.value || "\u2014"}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  /* Desktop: table */
                  <TableContainer
                    data-testid={`settings-table-${groupName.toLowerCase()}`}
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 2,
                      "& .MuiTableCell-head": {
                        bgcolor: "grey.50",
                        fontWeight: 600,
                        color: "text.secondary",
                      },
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t("configuration.settings.name")}
                          </TableCell>
                          <TableCell>
                            {t("configuration.settings.value")}
                          </TableCell>
                          <TableCell>
                            {t("configuration.settings.enabled")}
                          </TableCell>
                          <TableCell align="right">
                            {t("configuration.settings.actions")}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {groupItems.map((config: any) => (
                          <TableRow
                            key={config.id}
                            data-testid={`settings-row-${config.id}`}
                            hover
                            onClick={() => setSelectedSetting(config)}
                            sx={{
                              cursor: "pointer",
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>
                              {config.name}
                            </TableCell>
                            <TableCell sx={{ color: "text.secondary" }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                }}
                              >
                                {config.value}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Switch
                                data-testid={`settings-toggle-${config.id}`}
                                size="small"
                                checked={config.enabled}
                                onChange={(event) => {
                                  event.stopPropagation()
                                  handleToggleEnabled(config)
                                }}
                                color="primary"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={1}
                                justifyContent="flex-end"
                              >
                                <IconButton
                                  data-testid={`settings-edit-${config.id}-btn`}
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    handleEdit(config)
                                  }}
                                  color="primary"
                                  sx={{
                                    bgcolor: "primary.50",
                                    "&:hover": { bgcolor: "primary.100" },
                                  }}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                  data-testid={`settings-delete-${config.id}-btn`}
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    setDeleteConfirmId(config.id)
                                  }}
                                  color="error"
                                  sx={{
                                    bgcolor: "error.50",
                                    "&:hover": { bgcolor: "error.100" },
                                  }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )
          })}
        </Stack>
      )}

      <Drawer
        anchor="right"
        open={!!selectedSetting}
        onClose={() => setSelectedSetting(null)}
        PaperProps={{
          ...uiPaper("UI-DLV2-172"),
          sx: {
            width: 620,
            maxWidth: "100vw",
            borderTopLeftRadius: 12,
            borderBottomLeftRadius: 12,
          },
        }}
      >
        <Stack sx={{ p: 3, height: "100%" }} spacing={2}>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              {t("configuration.settings.detailTitle", "Setting details")}
            </Typography>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {selectedSetting?.name}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.75 }}
            >
              {t("configuration.settings.description", "Description")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                lineHeight: 1.65,
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent
              commodo, massa nec fermentum malesuada, urna augue ullamcorper
              lorem, a volutpat est magna et ligula. Integer suscipit eros id
              sem congue, a posuere nunc vestibulum.
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 0.75 }}
            >
              {t("configuration.settings.value", "Value")}
            </Typography>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {selectedSetting?.value || "\u2014"}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {t("configuration.settings.enabled", "Enabled")}
            </Typography>
            <Switch
              checked={!!selectedSetting?.enabled}
              onChange={async () => {
                if (!selectedSetting) return
                await handleToggleEnabled(selectedSetting)
                setSelectedSetting({
                  ...selectedSetting,
                  enabled: !selectedSetting.enabled,
                })
              }}
              color="primary"
            />
          </Box>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            onClick={() => setSelectedSetting(null)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("close")}
          </Button>
        </Stack>
      </Drawer>

      {/* Create/Edit Dialog */}
      <Dialog
        data-testid="setting-form-dialog"
        open={formOpen}
        onClose={handleFormClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          ...uiPaper("UI-DLV2-173"),
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          {editingId
            ? t("configuration.settings.edit")
            : t("configuration.settings.create")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1, pt: 1 }}>
            <TextField
              data-testid="setting-name-input"
              label={t("configuration.settings.name")}
              fullWidth
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              variant="outlined"
            />
            <TextField
              data-testid="setting-value-input"
              label={t("configuration.settings.value")}
              fullWidth
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              variant="outlined"
              multiline
              rows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-testid="setting-form-cancel-btn"
            onClick={handleFormClose}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="setting-form-submit-btn"
            variant="contained"
            onClick={handleFormSubmit}
            disabled={!form.name.trim()}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            {t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog
        data-testid="setting-delete-dialog"
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        PaperProps={{
          ...uiPaper("UI-DLV2-174"),
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t("configuration.settings.deleteTitle")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("configuration.settings.deleteConfirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            data-testid="setting-delete-cancel-btn"
            onClick={() => setDeleteConfirmId(null)}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("cancel")}
          </Button>
          <Button
            data-testid="setting-delete-confirm-btn"
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
